import {
  Breadcrumbs,
  Button,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { Box, SxProps } from '@mui/system';
import { Link as RouterLink } from 'react-router-dom';
import { useHistory } from 'react-router';
import AddIcon from '@mui/icons-material/Add';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { DataTable, ColumnType } from '../DataTable';
import { useEffect, useState, useMemo, ChangeEvent, MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import {
  exportFunds,
  freezeAccess,
  fundAccess,
  getFundDrafts,
  getFundsList,
  updateFreezeStatus,
  updateFund,
} from '../../redux-store/actions/funds';
import { downloadBlobFile } from '../../utils/utilityFunctions';
import {
  accessType,
  freezeAccessType,
  FundDraft,
  FundProps,
  GetFundsListResponseBody,
} from '../../redux-store/types/funds';
import { ConfirmationDialog } from '../commonComponents';
import { ListPaginationBar } from '../common/ListPagination';
import FundSetupStatsCards from './FundSetupStatsCards';
import {
  AccessToggle,
  RowActionsMenu,
  userMgmtBoxStyles,
  userMgmtRowStyles,
} from '../userManagement/userMgmtListStyles';
import { useDebounce } from '../../utils/useDebounce';
import { FUND_DRAFT_STEP_LABELS, normalizeFundDraftList } from './fundDraftUtils';

const fundListTableHeaderStyles: SxProps = {
  '.MuiTableCell-head': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: 500,
    color: '#1F53BE',
    backgroundColor: '#F6F9FD',
    borderBottom: '1px solid #E8ECF0',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
    overflowWrap: 'normal',
  },
};

const fundListTableBodyStyles: SxProps = {
  '.MuiTableCell-root': {
    borderBottom: '1px solid #F2F4F7',
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: 400,
    color: '#111927',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
    overflowWrap: 'normal',
  },
};

const fundListBoxStyles: SxProps = {
  ...userMgmtBoxStyles,
  width: '100%',
  overflowX: 'auto',
  '& table': {
    width: '100%',
    minWidth: 1080,
    tableLayout: 'auto',
  },
};

const filterSelectSx = {
  minWidth: 140,
  fontFamily: 'Inter, sans-serif',
  fontSize: 14,
  borderRadius: '8px',
  bgcolor: '#FFFFFF',
  color: '#374151',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E5E7EB',
    borderRadius: '8px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D5DB' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#2057A6',
    borderRadius: '8px',
  },
  '& .MuiSelect-select': {
    py: 1,
    pl: 2,
    pr: '36px !important',
  },
};

const DraftStatusPill = (): JSX.Element => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: 1.5,
      py: 0.5,
      borderRadius: '9999px',
      fontFamily: 'Inter, sans-serif',
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      bgcolor: '#FEF3C7',
      color: '#B54708',
    }}>
    Pending
  </Box>
);

const FundStatusPill = ({ frozen }: { frozen: boolean }): JSX.Element => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: 1.5,
      py: 0.5,
      borderRadius: '9999px',
      fontFamily: 'Inter, sans-serif',
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      bgcolor: frozen ? '#F3F4F6' : '#ECFDF5',
      color: frozen ? '#374151' : '#027A48',
    }}>
    {frozen ? 'Frozen' : 'Active'}
  </Box>
);

export const AccessHeaderForFund = (): JSX.Element => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box display="flex" flexDirection="column">
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            color: '#1F53BE',
            fontWeight: 500,
          }}
          component="span">
          Access
          <InfoOutlinedIcon
            color="info"
            fontSize="small"
            onClick={() => setOpen(true)}
            sx={{ position: 'relative', top: 4, ml: 0.5, cursor: 'pointer' }}
          />
        </Typography>
      </Box>
      <Dialog
        onClose={() => setOpen(false)}
        open={open}
        sx={{
          '.MuiPaper-root ': {
            maxWidth: 550,
            p: { xs: 2, sm: '15px 20px 45px' },
            borderRadius: '10px',
            height: 'unset',
            overflowY: 'auto',
          },
        }}>
        <IconButton onClick={() => setOpen(false)} sx={{ alignSelf: 'flex-end' }}>
          <CloseIcon fontSize="medium" />
        </IconButton>
        <Box sx={{ mb: 2, textAlign: 'center', '.MuiSvgIcon-root': { fontSize: '3.1875rem' } }}>
          <InfoOutlinedIcon color="info" fontSize="large" />
        </Box>
        <Box
          sx={{
            width: { xs: '100%', sm: '80%' },
            mx: 'auto',
            '.MuiTypography-root': {
              fontSize: 20,
              fontWeight: 500,
              textAlign: 'center',
            },
          }}>
          <Typography sx={{ color: 'secondary.main', lineHeight: '35px', mt: 2 }}>
            Please ensure that you have at least one active fund before disabling the fund.
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export const FreezeHeaderForFund = (): JSX.Element => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box display="flex" flexDirection="column">
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            color: '#1F53BE',
            fontWeight: 500,
          }}
          component="span">
          Freeze
          <InfoOutlinedIcon
            color="info"
            fontSize="small"
            onClick={() => setOpen(true)}
            sx={{ position: 'relative', top: 4, ml: 0.5, cursor: 'pointer' }}
          />
        </Typography>
      </Box>
      <Dialog
        onClose={() => setOpen(false)}
        open={open}
        sx={{
          '.MuiPaper-root ': {
            maxWidth: 550,
            p: { xs: 2, sm: '15px 20px 45px' },
            borderRadius: '10px',
            height: 'unset',
            overflowY: 'auto',
          },
        }}>
        <IconButton onClick={() => setOpen(false)} sx={{ alignSelf: 'flex-end' }}>
          <CloseIcon fontSize="medium" />
        </IconButton>
        <Box sx={{ mb: 2, textAlign: 'center', '.MuiSvgIcon-root': { fontSize: '3.1875rem' } }}>
          <InfoOutlinedIcon color="info" fontSize="large" />
        </Box>
        <Box
          sx={{
            width: { xs: '100%', sm: '80%' },
            mx: 'auto',
            '.MuiTypography-root': {
              fontSize: 20,
              fontWeight: 500,
              textAlign: 'center',
            },
          }}>
          <Typography sx={{ color: 'secondary.main', lineHeight: '35px', mt: 2 }}>
            Please ensure that you have at least one un-freeze fund before disabling the fund.
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export default function FundsList(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fundData, setFundData] = useState<FundProps[] | null>(null);
  const [draftData, setDraftData] = useState<FundDraft[]>([]);
  const [access, setAccess] = useState(false);
  const [_freezeAccess, setFreezeAccess] = useState(false);
  const [openDialog, setDialog] = useState<{ FundData: FundProps } | null>(null);
  const [openFreezeDialog, setFreezeDialog] = useState<{ FundData: FundProps } | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [schemeCodeFilter, setSchemeCodeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const debounceSearch = useDebounce(search, 300);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [isDownloadingFunds, setIsDownloadingFunds] = useState(false);

  const handleDownloadFunds = async () => {
    if (isDownloadingFunds) return;
    try {
      setIsDownloadingFunds(true);
      const blob = (await dispatch(exportFunds())) as unknown as Blob;
      downloadBlobFile(blob, 'aif-onboarding-Funds.xlsx');
    } catch (e) {
      console.error((e as Error).message);
    } finally {
      setIsDownloadingFunds(false);
    }
  };

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        setLoading(true);
        const res = (await dispatch(getFundsList())) as unknown as GetFundsListResponseBody;
        const { fundSetupAccess } = (await dispatch(fundAccess())) as unknown as accessType;
        const { schemeFreezeAccess } = (await dispatch(
          freezeAccess()
        )) as unknown as freezeAccessType;
        let drafts: FundDraft[] = [];
        try {
          const draftRes = await dispatch(getFundDrafts({ status: 'DRAFT' }));
          drafts = normalizeFundDraftList(draftRes);
        } catch (draftError) {
          console.log((draftError as Error).message);
        }
        if (!isComponentActive) return;
        setAccess(fundSetupAccess);
        setFreezeAccess(schemeFreezeAccess);
        setFundData(res.funds);
        setDraftData(drafts);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.log((e as Error).message);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, []);

  const funds = fundData || [];

  const pendingDrafts = useMemo(() => {
    const existingSchemeCodes = new Set(
      funds
        .map((fund) =>
          String(fund.schemeCode || '')
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    );
    return draftData.filter((draft) => {
      const status = String(draft.status || 'DRAFT').toUpperCase();
      if (status !== 'DRAFT') return false;
      if (draft.schemeId || draft.planId) return false;
      const draftSchemeCode = String(draft.step1Data?.schemeCode || '')
        .trim()
        .toLowerCase();
      if (draftSchemeCode && existingSchemeCodes.has(draftSchemeCode)) return false;
      return true;
    });
  }, [draftData, funds]);

  const schemeCodeOptions = useMemo(() => {
    const codes = Array.from(
      new Set(funds.map((f) => f.schemeCode).filter((code): code is string => Boolean(code)))
    ).sort();
    return codes;
  }, [funds]);

  const filteredFunds = useMemo(() => {
    const q = debounceSearch.trim().toLowerCase();
    return funds.filter((fund) => {
      if (statusFilter === 'active' && (!fund.isActive || fund.freezeStatus)) return false;
      if (statusFilter === 'frozen' && fund.isActive && !fund.freezeStatus) return false;
      if (schemeCodeFilter !== 'all' && fund.schemeCode !== schemeCodeFilter) return false;
      if (q && !(fund.schemeName || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [funds, statusFilter, schemeCodeFilter, debounceSearch]);

  const exportCsv = () => {
    const headers = [
      'Scheme Name',
      'AIF/AMC Code',
      'Scheme Code',
      'Access',
      'Total Plans',
      'Status',
    ];
    const rows = filteredFunds.map((f) => [
      f.schemeName || '',
      f.amcCode || '',
      f.schemeCode || '',
      f.isActive ? 'Enabled' : 'Disabled',
      String(f.plans?.length || 0),
      !f.isActive || f.freezeStatus ? 'Frozen' : 'Active',
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'funds-list.csv';
    link.click();
    URL.revokeObjectURL(url);
    setExportAnchorEl(null);
  };

  const noOfPages = Math.max(1, Math.ceil(filteredFunds.length / rowsPerPage) || 1);

  useEffect(() => {
    if (currentPage > noOfPages) setCurrentPage(1);
  }, [noOfPages, currentPage]);

  const pagedFunds = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredFunds.slice(start, start + rowsPerPage);
  }, [filteredFunds, currentPage, rowsPerPage]);

  const fundStats = {
    totalFunds: funds.length,
    activeFunds: funds.filter((f) => f.isActive).length,
    disabledFunds: funds.filter((f) => !f.isActive).length,
    totalPlans: funds.reduce((sum, f) => sum + (f.plans?.length || 0), 0),
  };

  const handleToggleField = async (
    fund: FundProps,
    field: 'isActive' | 'freezeStatus',
    updateFn: typeof updateFund,
    setDialogState: (value: null) => void
  ) => {
    setDialogState(null);
    try {
      const updatedRes = (await dispatch(
        updateFn(Number(fund.id), {
          ...fund,
          [field]: !fund[field],
        })
      )) as unknown as FundProps;

      setFundData((prev) => {
        if (!prev) return null;
        return prev.map((f) => {
          if (Number(f.id) === Number(updatedRes.id)) {
            return { ...f, ...updatedRes };
          }
          return f;
        });
      });
    } catch (e) {
      console.error((e as Error).message);
    }
  };

  const columns: ColumnType[] = useMemo(
    () => [
      {
        header: 'Scheme Name',
        align: 'left',
        headerAlign: 'left',
        minWidth: 220,
        valueGetter: (row: FundProps) => row.schemeName || 'N/A',
      },
      {
        header: 'AIF/AMC Code',
        align: 'left',
        headerAlign: 'left',
        minWidth: 120,
        valueGetter: (row: FundProps) => row.amcCode || 'N/A',
      },
      {
        header: 'Scheme Code',
        align: 'left',
        headerAlign: 'left',
        minWidth: 120,
        valueGetter: (row: FundProps) => row.schemeCode || 'N/A',
      },
      {
        header: AccessHeaderForFund,
        align: 'left',
        headerAlign: 'left',
        minWidth: 150,
        renderCell: (row: FundProps) => (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
            <AccessToggle
              checked={!!row.isActive}
              onChange={() => setDialog({ FundData: row })}
              disabled={!access}
            />
          </Box>
        ),
      },
      {
        header: FreezeHeaderForFund,
        align: 'left',
        headerAlign: 'left',
        minWidth: 150,
        renderCell: (row: FundProps) => (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
            <AccessToggle
              checked={!!row.freezeStatus}
              onChange={() => setFreezeDialog({ FundData: row })}
              disabled={!_freezeAccess}
              enabledLabel="Frozen"
              disabledLabel="Unfrozen"
            />
          </Box>
        ),
      },
      {
        header: 'Total Plans',
        align: 'left',
        headerAlign: 'left',
        minWidth: 110,
        valueGetter: (row: FundProps) => row.plans?.length || 0,
      },
      {
        header: 'Status',
        align: 'left',
        headerAlign: 'left',
        minWidth: 100,
        renderCell: (row: FundProps) => (
          <FundStatusPill frozen={!row.isActive || !!row.freezeStatus} />
        ),
      },
      {
        header: 'Actions',
        align: 'left',
        headerAlign: 'left',
        minWidth: 72,
        renderCell: (row: FundProps) => (
          <RowActionsMenu
            items={[
              {
                label: 'View Scheme Details',
                icon: '/images/view-application.svg',
                to: { pathname: 'fund-details', state: { fundId: row.id } },
              },
              {
                label: 'View Plans',
                icon: '/images/rm.svg',
                to: { pathname: 'view-plans', state: { fundId: row.id } },
              },
              ...(access || _freezeAccess
                ? [
                    {
                      label: 'Edit Scheme Details',
                      icon: '/images/edit.svg',
                      to: { pathname: 'edit-fund', state: { fundId: row.id } },
                    },
                  ]
                : []),
            ]}
          />
        ),
      },
    ],
    [access, _freezeAccess]
  );

  const draftColumns: ColumnType[] = useMemo(
    () => [
      {
        header: 'Scheme Name',
        align: 'left',
        headerAlign: 'left',
        minWidth: 220,
        valueGetter: (row: FundDraft) =>
          (row.step1Data?.schemeName as string) || (row.step1Data?.schemeCode as string) || 'N/A',
      },
      {
        header: 'Scheme Code',
        align: 'left',
        headerAlign: 'left',
        minWidth: 140,
        valueGetter: (row: FundDraft) => (row.step1Data?.schemeCode as string) || 'N/A',
      },
      {
        header: 'Current Step',
        align: 'left',
        headerAlign: 'left',
        minWidth: 180,
        valueGetter: (row: FundDraft) =>
          FUND_DRAFT_STEP_LABELS[row.currentStep] || `Step ${row.currentStep || 1}`,
      },
      {
        header: 'Status',
        align: 'left',
        headerAlign: 'left',
        minWidth: 110,
        renderCell: () => <DraftStatusPill />,
      },
      {
        header: 'Last Updated',
        align: 'left',
        headerAlign: 'left',
        minWidth: 180,
        valueGetter: (row: FundDraft) =>
          row.updatedAt ? new Date(row.updatedAt).toLocaleString() : 'N/A',
      },
      {
        header: 'Actions',
        align: 'left',
        headerAlign: 'left',
        minWidth: 72,
        renderCell: (row: FundDraft) => (
          <RowActionsMenu
            items={[
              {
                label: 'Continue Editing',
                icon: '/images/edit.svg',
                to: {
                  pathname: '/funds-setup/add-fund',
                  state: { draftId: String(row.id) },
                },
              },
            ]}
          />
        ),
      },
    ],
    []
  );

  return (
    <>
      <Box sx={{ bgcolor: 'transparent', px: { xs: 2, sm: 4 }, pt: { xs: 2, sm: 3 }, pb: 3 }}>
        <Breadcrumbs
          separator=">"
          aria-label="breadcrumb"
          sx={{
            mb: 1.5,
            '& .MuiBreadcrumbs-separator': { mx: 0.75, color: '#6C737F' },
            '.MuiTypography-root, .MuiLink-root': {
              fontSize: 14,
              color: '#111927',
              fontFamily: 'Inter, sans-serif',
            },
          }}>
          <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
            Home
          </Link>
          <Typography sx={{ fontWeight: 500, color: '#6C737F !important' }}>Fund Set-Up</Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2.5,
          }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: 20, sm: 24 },
              fontWeight: 700,
              color: '#092056',
              lineHeight: 1.2,
            }}>
            Fund Set-Up
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleDownloadFunds}
              disabled={isDownloadingFunds}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: 14,
                lineHeight: 1,
                textTransform: 'none',
                color: '#2057A6',
                borderColor: '#2057A6',
                borderRadius: '8px',
                px: 2,
                py: 1,
                bgcolor: '#FFFFFF',
                '&:hover': {
                  borderColor: '#1A4A8C',
                  bgcolor: '#F8FAFC',
                },
              }}>
              {isDownloadingFunds ? 'Downloading...' : 'Download Funds Data'}
            </Button>
            {(access || _freezeAccess) && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: 14,
                  lineHeight: 1,
                  textTransform: 'none',
                  bgcolor: '#2057A6',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  px: 2,
                  py: 1.25,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#1A4A8C',
                    boxShadow: 'none',
                  },
                }}
                onClick={() => history.push('add-fund')}>
                Add Funds
              </Button>
            )}
          </Box>
        </Box>

        <FundSetupStatsCards stats={fundStats} />

        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 18,
            fontWeight: 700,
            color: '#111927',
            mb: 2,
          }}>
          Pending Drafts
        </Typography>
        <Box
          sx={{
            bgcolor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #E8ECF0',
            overflow: 'hidden',
            mb: 3,
          }}>
          <DataTable
            tableData={pendingDrafts}
            tableHeader={draftColumns}
            isLoading={isLoading}
            currentPage={undefined}
            noOfPages={1}
            renderAdditionalRow={false}
            tableBodyCustomStyles={fundListTableBodyStyles}
            rowCustomStyles={userMgmtRowStyles}
            tableHeaderCustomStyles={fundListTableHeaderStyles}
            boxStyles={fundListBoxStyles}
          />
        </Box>

        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 18,
            fontWeight: 700,
            color: '#111927',
            mb: 2,
          }}>
          Funds List
        </Typography>

        <Box
          sx={{
            bgcolor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #F2F4F7',
            boxShadow: '0px 4px 16px rgba(26, 43, 60, 0.04)',
            p: { xs: 2, sm: 2.5 },
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              mb: 2.5,
            }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                displayEmpty
                value={statusFilter}
                onChange={(e: SelectChangeEvent) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                IconComponent={KeyboardArrowDownIcon}
                renderValue={(selected) => {
                  if (selected === 'all') return 'Status';
                  if (selected === 'active') return 'Active';
                  if (selected === 'frozen') return 'Frozen';
                  return 'Status';
                }}
                sx={filterSelectSx}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="frozen">Frozen</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                displayEmpty
                value={schemeCodeFilter}
                onChange={(e: SelectChangeEvent) => {
                  setSchemeCodeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                IconComponent={KeyboardArrowDownIcon}
                renderValue={(selected) => (selected === 'all' ? 'Scheme Code' : String(selected))}
                sx={filterSelectSx}>
                <MenuItem value="all">All</MenuItem>
                {schemeCodeOptions.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by Fund Name"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchSharpIcon sx={{ color: '#9CA3AF', fontSize: 20 }} aria-hidden />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                minWidth: { xs: '100%', sm: 220 },
                maxWidth: 420,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 14,
                  bgcolor: '#FFFFFF',
                  '& fieldset': { borderColor: '#E5E7EB', borderRadius: '8px' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: '#2057A6', borderRadius: '8px' },
                },
              }}
            />

            <Button
              variant="outlined"
              endIcon={<KeyboardArrowDownIcon />}
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={(e: MouseEvent<HTMLElement>) => setExportAnchorEl(e.currentTarget)}
              sx={{
                ml: { sm: 'auto' },
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: 14,
                textTransform: 'none',
                color: '#344054',
                borderColor: '#E5E7EB',
                borderRadius: '8px',
                bgcolor: '#FFFFFF',
                px: 2,
                py: 0.9,
                boxShadow: 'none',
                '&:hover': {
                  borderColor: '#D1D5DB',
                  bgcolor: '#F9FAFB',
                  boxShadow: 'none',
                },
              }}>
              Export
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={() => setExportAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <MenuItem
                onClick={exportCsv}
                sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#6C737F' }}>
                Export as CSV
              </MenuItem>
            </Menu>
          </Box>

          <DataTable
            tableData={pagedFunds}
            tableHeader={columns}
            isLoading={isLoading}
            currentPage={undefined}
            noOfPages={1}
            renderAdditionalRow={false}
            tableBodyCustomStyles={fundListTableBodyStyles}
            rowCustomStyles={userMgmtRowStyles}
            tableHeaderCustomStyles={fundListTableHeaderStyles}
            boxStyles={fundListBoxStyles}
          />

          {!isLoading && filteredFunds.length > 0 && (
            <ListPaginationBar
              pageCount={noOfPages}
              page={currentPage}
              onPageChange={(_e: ChangeEvent<unknown>, value: number) => setCurrentPage(value)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(rows) => {
                setRowsPerPage(rows);
                setCurrentPage(1);
              }}
              total={filteredFunds.length}
            />
          )}
        </Box>
      </Box>

      <ConfirmationDialog
        message={`Are you sure you want to ${
          openDialog?.FundData?.isActive ? 'disable' : 'enable'
        } this Fund?`}
        open={openDialog !== null}
        setOpen={() => setDialog(null)}
        onSave={() => {
          if (!openDialog?.FundData) return;
          handleToggleField(openDialog.FundData, 'isActive', updateFund, () => setDialog(null));
        }}
        onCancel={() => setDialog(null)}
      />
      <ConfirmationDialog
        message={`Are you sure you want to ${
          openFreezeDialog?.FundData?.freezeStatus ? 'unfreeze' : 'freeze'
        } this Fund?`}
        open={openFreezeDialog !== null}
        setOpen={() => setFreezeDialog(null)}
        onSave={() => {
          if (!openFreezeDialog?.FundData) return;
          handleToggleField(openFreezeDialog.FundData, 'freezeStatus', updateFreezeStatus, () =>
            setFreezeDialog(null)
          );
        }}
        onCancel={() => setFreezeDialog(null)}
      />
    </>
  );
}
